import { ControlledAtmosphereTemperaturesFilter } from '..';
import { flatten, maxBy, minBy } from 'lodash';

import { GetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';
import { Container_Insights_Bookings_Expanded_Bool_Exp } from '@/graphql/types';

import { RefrigerationUnitModelsFilter } from './refrigerationUnitModels';
import { EmissionsDashboardStore, store } from './store';

const setAssetIdsFromBookings = (
  bookings: GetBookingsExpandedQuery['container_insights_bookings_expanded'],
) => {
  const assetIds: string[] = [];
  const assetsModel: { [assetId: string]: string } = {};
  const assetIdsByModel: { [modelName: string]: string[] } = {};
  // cache results to avoid unnecessary loops
  const controllerMap: {
    [key: string]: RefrigerationUnitModelsFilter | undefined;
  } = {};

  // try to build our dataset in one pass
  for (const booking of bookings) {
    // store asset id
    if (!booking.reefer_id) {
      throw new Error(`Booking ${booking.internal_booking_id}: reefer is null`);
    }
    const reeferId = booking.reefer_id;
    if (!assetIds.includes(reeferId)) {
      assetIds.push(reeferId);
    }

    const bookingModel = booking.refrigeration_unit_model || 'unknown';
    const model = controllerMap[bookingModel];
    if (model) {
      // store model name for each asset
      if (!assetsModel[reeferId]) {
        assetsModel[reeferId] = model.name;
      }
      // store asset ids by model name
      if (!assetIdsByModel[model.name]) {
        assetIdsByModel[model.name] = [];
      }
      if (!assetIdsByModel[model.name].includes(reeferId)) {
        assetIdsByModel[model.name].push(reeferId);
      }
    }
  }

  // this will force refresh
  store.assetIds = assetIds;
  store.assetIdsByModel = assetIdsByModel;
  store.assetsModel = assetsModel;
};

const getBookingsExpandedQueryWhereClauseFromFilters = (
  snap: EmissionsDashboardStore,
) => {
  const {
    selectedRefrigerationUnitModels,
    selectedTemperatures,
    selectedTradelanes,
    selectedAssets,
    selectedReeferAges,
    daterange,
  } = snap;

  let refrigeration_unit_model = {};
  if (selectedRefrigerationUnitModels.length > 0) {
    refrigeration_unit_model = {
      refrigeration_unit_model: {
        _in: flatten(
          selectedRefrigerationUnitModels.map((model) => model.name),
        ),
      },
    };
  }

  const selectedSetpointTemperatures = selectedTemperatures.filter(
    (temperature) =>
      temperature.id !== ControlledAtmosphereTemperaturesFilter.id,
  );
  let temperature_setpoint_id = {};
  if (selectedSetpointTemperatures.length > 0) {
    temperature_setpoint_id = {
      temperature_setpoint_id: {
        _in: selectedSetpointTemperatures.map((temperature) => temperature.id),
      },
    };
  }

  const controlledAtmosphereTemperature = selectedTemperatures.find(
    (temperature) =>
      temperature.id === ControlledAtmosphereTemperaturesFilter.id,
  );
  let controlled_atmosphere = {};
  if (
    controlledAtmosphereTemperature &&
    controlledAtmosphereTemperature.selected
  ) {
    controlled_atmosphere = {
      ca: {
        _eq: true,
      },
    };
  }

  let tradelane = {};
  if (selectedTradelanes.length > 0) {
    tradelane = {
      tradelane: {
        _in: selectedTradelanes.map((tradelane) => tradelane.label),
      },
    };
  }

  let reefer_ages = {};
  if (selectedReeferAges.length > 0) {
    reefer_ages = {
      reefer: {
        age_id: {
          _in: selectedReeferAges.map((age) => age.id),
        },
      },
    };
  }

  let reefer_id = {};
  if (selectedAssets.length > 0) {
    reefer_id = {
      reefer_id: {
        _in: selectedAssets.map((asset) => asset.id),
      },
    };
  }

  const where: Container_Insights_Bookings_Expanded_Bool_Exp = {
    start_datetime: {
      _gte: daterange.from.toISOString(),
    },
    end_datetime: {
      _lte: daterange.to.toISOString(),
    },
    ...refrigeration_unit_model,
    ...temperature_setpoint_id,
    ...controlled_atmosphere,
    ...tradelane,
    ...reefer_ages,
    ...reefer_id,
  };

  return where;
};

export {
  setAssetIdsFromBookings,
  getBookingsExpandedQueryWhereClauseFromFilters,
};
